import React, { useMemo, useState } from "react";
import { StyledSwipeableDrawer, StyledTypography } from "../../../../styled-mui";
import { StyledLink } from "../../../../styled-mui/link/styled-link";
import { useSelector } from "react-redux";
import { GetStaticLinksSelector } from "../../../../redux/selectors/category";
import { EXTERNAL_LINK, LINK_USAGE, NAVIGATION_IDENTIFIERS } from "../../../../constants/marketing";
import { Box } from "@mui/material";
import { CLICK } from "../../../../_foundation/constants/gtm";
import AsyncCall from "../../../../_foundation/gtm/async.service";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Divider from "@mui/material/Divider";
import { ProduitsIcon, AccesProIcon } from "../../../header/NavgationBarSvg";
import { getSite } from "../../../../_foundation/hooks/useSite";
import MobileMenuWrapper from "./MobileMenuWrapper";
import MobileProductsMenu from "./sub-menus/MobileProuctsMenu/MobileProductsMenu";
import MobileInspirationsMenu from "./sub-menus/MobileInspirationsMenu";
import MobilePostEtServicesMenu from "./sub-menus/MobilePoseEtServicesMenu";
import MobileOutilsMenu from "./sub-menus/MobileOutilsMenu";
import { ImageLayout } from "../../../elements/image/ImageLayout";
import storeUtil from "../../../../utils/storeUtil";
import { HUBPROMO } from "../../../../constants/routes";

interface Props {
  open: boolean;
  closeMenu: () => any;
  menuLinksData: Record<string, any>;
}

// ICON MAP FOR LINKS/MENUS HARDCODED FROM FRONTEND
const IconMap = {
  [NAVIGATION_IDENTIFIERS.PRODUCTS]: ProduitsIcon,
  [NAVIGATION_IDENTIFIERS.PRO_WEBSITE]: AccesProIcon,
};

const MobileMenu: React.FC<Props> = ({ open = false, closeMenu, menuLinksData }) => {
  const mySite = getSite();
  const staticLinksRedux: any = useSelector(GetStaticLinksSelector);
  const [selectedTab, setSelectedTab] = useState("");
  const PROSITE_LINK = mySite?.storeCfg?.userData?.PRO_SITE_URL;

  const menuLinks = useMemo(() => {
    const headerLinks = (staticLinksRedux && staticLinksRedux?.headerLinks) || [];
    return [
      {
        usage: LINK_USAGE.STANDARD,
        seo: {
          label: "Produits",
          target: "",
          type: "",
          url: "",
          identifier: NAVIGATION_IDENTIFIERS.PRODUCTS,
        },
      },
      ...headerLinks,
      {
        usage: "",
        seo: {
          label: "Accès Pro",
          target: "",
          type: "external",
          url: PROSITE_LINK,
          identifier: NAVIGATION_IDENTIFIERS.PRO_WEBSITE,
        },
      },
    ];
  }, [staticLinksRedux, PROSITE_LINK]);

  const closeAllMobileMenu = () => {
    setSelectedTab("");
    closeMenu && closeMenu();
  };

  const closeCurrentMenu = () => {
    setSelectedTab("");
  };

  const onMenuItemClick = (menuLink: Record<string, any>) => {
    const isAPageLink = menuLink?.usage !== LINK_USAGE.STANDARD;
    const isPromoLink = menuLink?.usage === LINK_USAGE.PROMO;
    AsyncCall.sendClick(CLICK.NAV, {
      click_section: "menu",
      nav_level_1: menuLink?.seo?.label,
      nav_level_2: "",
      nav_level_3: "",
      nav_type: isPromoLink ? "promo_block" : isAPageLink ? "link" : "cta",
      btn_label: menuLink?.seo?.label || "",
      click_destination: isPromoLink ? HUBPROMO : isAPageLink ? menuLink?.seo?.url : "",
    });
    setSelectedTab(menuLink?.seo?.identifier || "");
    if (isAPageLink) {
      closeAllMobileMenu();
    }
  };

  return (
    <>
      <StyledSwipeableDrawer
        open={open}
        onClose={closeAllMobileMenu}
        onOpen={() => null}
        anchor="left"
        className="header-menu"
        data-testid="header-menu-drawer-element">
        <MobileMenuWrapper title="MENU" entireMenuCloseHandler={closeAllMobileMenu}>
          {menuLinks.map((menuLink: Record<string, any>, index: number) => {
            const isAPageLink = menuLink?.usage !== LINK_USAGE.STANDARD;
            const isAnExternalLink = menuLink.seo?.type === EXTERNAL_LINK;
            const Icon = IconMap?.[menuLink?.seo?.identifier] || null;
            return (
              <React.Fragment key={menuLink.seo?.label}>
                <StyledLink
                  className="menu-mobile-link-top"
                  to={isAPageLink ? menuLink.seo?.url : "#"}
                  href={isAPageLink && isAnExternalLink ? menuLink.seo?.url : null}
                  onClick={() => onMenuItemClick(menuLink)}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      {Icon && !menuLink?.icon && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                          }}>
                          <Icon /> {/* USE HARDCODED ICON */}
                        </Box>
                      )}
                      {menuLink?.icon && (
                        <ImageLayout
                          src={storeUtil.getUrlPicture(`${menuLink.icon}`, "")}
                          height={24}
                          width={24}
                          styles={{ margin: "unset" }}
                        />
                      )}
                      <StyledTypography variant="body1">{menuLink.seo?.label}</StyledTypography>
                    </Box>
                    <ChevronRightIcon sx={{ fontSize: "20px", color: "black" }} />
                  </Box>
                </StyledLink>
                <Box my={1.25}>
                  <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.1)" }} />
                </Box>
              </React.Fragment>
            );
          })}
        </MobileMenuWrapper>
      </StyledSwipeableDrawer>

      {/* SUB-MENUS, USING "open" STATE TO PREVENT HEAVY DOM RENDERING ON INITIAL PAGE */}
      {open && (
        <>
          <MobileProductsMenu
            open={selectedTab === NAVIGATION_IDENTIFIERS.PRODUCTS}
            closeCurrentMenu={closeCurrentMenu}
            entireMenuCloseHandler={closeAllMobileMenu}
            menuLinksData={menuLinksData}
          />
          <MobileInspirationsMenu
            open={selectedTab === NAVIGATION_IDENTIFIERS.INSPIRATIONS}
            closeCurrentMenu={closeCurrentMenu}
            entireMenuCloseHandler={closeAllMobileMenu}
            menuLinksData={menuLinksData}
          />
          <MobilePostEtServicesMenu
            open={selectedTab === NAVIGATION_IDENTIFIERS.POSE_ET_SERVICE}
            closeCurrentMenu={closeCurrentMenu}
            entireMenuCloseHandler={closeAllMobileMenu}
            menuLinksData={menuLinksData}
          />
          <MobileOutilsMenu
            open={selectedTab === NAVIGATION_IDENTIFIERS.OUTILS}
            closeCurrentMenu={closeCurrentMenu}
            entireMenuCloseHandler={closeAllMobileMenu}
            menuLinksData={menuLinksData}
          />
        </>
      )}
    </>
  );
};

export default MobileMenu;
